import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { vaiTroNenTangRepository } from '../../../lib/repositories/vai-tro-nen-tang.repository';
import { useAuthStore } from '../../../stores/auth';
import { KHOA_VAI_TRO_NEN_TANG, thuLai } from './useVaiTroNenTang';

/** Tải lại ngữ cảnh nền tảng ngay, khi vừa đổi quyền của một vai trò. */
export function lamMoiNguCanhNenTang(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO_NEN_TANG.nguCanh });
}

/**
 * Ghi quyền tính từ ngữ cảnh vào hồ sơ đang đăng nhập.
 *
 * Gọi ngay trong `queryFn` thay vì đồng bộ bằng `useEffect`: mọi lần tải lại
 * (đổi tab quay về, hết `staleTime`) đều đi qua đây, nên vai trò vừa được gán
 * là thanh điều hướng đổi theo mà không có nhịp nào hồ sơ lệch dữ liệu vừa nhận.
 *
 * **Không** ghi `roles`: đó là nhãn hiển thị dưới tên ở thanh điều hướng, vẫn
 * suy từ realm role ở `lib/auth/oidc.ts` (ADR-0074 mục 9). Chỉ `permissions`
 * tới từ server.
 */
function apDung(userId: string, permissions: readonly string[]): void {
  useAuthStore.setState((s) =>
    s.user && s.user.id === userId ? { user: { ...s.user, permissions: [...permissions] } } : s,
  );
}

/**
 * Ngữ cảnh quyền của người đang đăng nhập ở **cổng nền tảng** (ADR-0074 mục 6).
 *
 * Thay chuỗi `if/else` mà `lib/auth/oidc.ts` từng dùng để đoán quyền từ realm
 * role ngay trong trình duyệt: quyền giờ do tenant-service tính, là hợp của
 * quyền suy từ realm role và quyền của vai trò nền tảng đã gán.
 *
 * Bản song song của `useNguCanhCuaToi` (cổng tenant, ADR-0034). Hai hook tách
 * nhau vì hai endpoint khác nhau và hình dạng trả về khác nhau: cổng nền tảng
 * không có `dich_vu`, `mods` hay `tenant_status`.
 *
 * `batBuoc = false` thì không gọi: tài khoản mẫu và tài khoản cổng tenant không
 * có ngữ cảnh nền tảng (xem `NguCanhGate`).
 */
export function useNguCanhNenTang(batBuoc: boolean) {
  const userId = useAuthStore((s) => s.user?.id);
  const vaiTroLop1 = useAuthStore((s) => s.user?.vaiTroLop1);

  return useQuery({
    queryKey: KHOA_VAI_TRO_NEN_TANG.nguCanh,
    queryFn: async () => {
      const ctx = await vaiTroNenTangRepository.nguCanh(vaiTroLop1 ?? []);
      if (userId) apDung(userId, ctx.permissions);
      return ctx;
    },
    enabled: batBuoc && Boolean(userId),
    staleTime: 60_000,
    retry: thuLai as (soLanDaThu: number, err: ApiError) => boolean,
  });
}
